import React, { useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { makeStyles } from "@material-ui/core/styles";
import { Helmet } from "react-helmet";
import ColorModeContext from "../../layout/themeContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => {
    const { mode } = theme;
    return {
        root: {
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
        },
        leftPanel: {
            width: "44%",
            height: "100vh",
            background: "linear-gradient(145deg, #1a1a2e 0%, #16213e 45%, #0f3460 80%, #162447 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            [theme.breakpoints.down("sm")]: { display: "none" },
        },
        leftContent: {
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "32px 40px",
            textAlign: "center",
        },
        logoImg: {
            width: "100%",
            maxWidth: "200px",
            height: "auto",
            maxHeight: "65px",
            margin: "0 auto 28px auto",
            filter: "drop-shadow(0 2px 12px rgba(0,0,0,0.5)) brightness(1.15)",
            content: "url(" + theme.calculatedLogoDark() + ")",
        },
        tagline: {
            color: "rgba(255,255,255,0.92)",
            fontWeight: 700,
            fontSize: "1.4rem",
            lineHeight: 1.3,
            marginBottom: "10px",
        },
        subTagline: {
            color: "rgba(255,255,255,0.45)",
            fontSize: "0.9rem",
            lineHeight: 1.7,
            maxWidth: "260px",
        },
        rightPanel: {
            flex: 1,
            height: "100vh",
            background: mode === "light" ? "#f8fafc" : "#0d1117",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 32px",
            overflowY: "auto",
        },
        formWrapper: {
            width: "100%",
            maxWidth: "400px",
        },
        eyebrow: {
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: mode === "light"
                ? `${theme.palette.primary.main}14`
                : `${theme.palette.primary.main}20`,
            color: theme.palette.primary.main,
            borderRadius: "20px",
            padding: "4px 14px",
            fontSize: "0.78rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            marginBottom: "16px",
        },
        title: {
            fontWeight: 700,
            fontSize: "1.8rem",
            color: mode === "light" ? "#0f172a" : "#f1f5f9",
            marginBottom: "8px",
            letterSpacing: "-0.03em",
        },
        subtitle: {
            color: mode === "light" ? "#64748b" : "#64748b",
            fontSize: "0.9rem",
            marginBottom: "28px",
            lineHeight: 1.6,
        },
        textField: {
            "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: mode === "light" ? "#ffffff" : "rgba(255,255,255,0.04)",
                "& fieldset": {
                    borderColor: mode === "light" ? "#e2e8f0" : "rgba(255,255,255,0.1)",
                    borderWidth: "1.5px",
                },
                "&:hover fieldset": { borderColor: `${theme.palette.primary.main}80` },
                "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main, borderWidth: "2px" },
                "&.Mui-focused": { boxShadow: `0 0 0 4px ${theme.palette.primary.main}14` },
            },
            "& .MuiInputLabel-root": {
                color: mode === "light" ? "#94a3b8" : "#475569",
                "&.Mui-focused": { color: theme.palette.primary.main },
            },
            "& .MuiOutlinedInput-input": {
                color: mode === "light" ? "#0f172a" : "#f1f5f9",
            },
            "& .MuiInputAdornment-root .MuiIconButton-root": {
                color: mode === "light" ? "#94a3b8" : "#475569",
                padding: "6px",
                borderRadius: "8px",
            },
        },
        submit: {
            marginTop: "24px",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
            color: "#ffffff",
            borderRadius: "12px",
            padding: "14px 0",
            fontSize: "15px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: `0 4px 16px ${theme.palette.primary.main}45`,
            "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 28px ${theme.palette.primary.main}55`,
            },
        },
        backLink: {
            display: "block",
            textAlign: "center",
            marginTop: "20px",
            color: theme.palette.primary.main,
            fontSize: "0.88rem",
            fontWeight: 600,
            cursor: "pointer",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
        },
        successBox: {
            textAlign: "center",
            padding: "20px 0",
        },
        errorBox: {
            background: mode === "light" ? "#fef2f2" : "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: "10px",
            padding: "12px 16px",
            marginBottom: "16px",
            color: "#ef4444",
            fontSize: "0.875rem",
        },
    };
});

const ResetPassword = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName } = colorMode;

    const token = new URLSearchParams(location.search).get("token");

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!password || password.length < 6) {
            setError("A senha deve ter pelo menos 6 caracteres.");
            return;
        }
        if (password !== confirm) {
            setError("As senhas não coincidem.");
            return;
        }
        if (!token) {
            setError("Token inválido. Solicite um novo link.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setSuccess(true);
        } catch (err) {
            const msg = err?.response?.data?.error || "Não foi possível redefinir a senha.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>{appName || "GoldTarget Chatbot"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>
            <CssBaseline />
            <div className={classes.root}>
                {/* Painel esquerdo */}
                <div className={classes.leftPanel}>
                    <div className={classes.leftContent}>
                        <img className={classes.logoImg} alt="logo" />
                        <Typography className={classes.tagline}>
                            Segurança em<br />primeiro lugar
                        </Typography>
                        <Typography className={classes.subTagline}>
                            Crie uma senha forte para proteger sua conta e manter seus atendimentos seguros.
                        </Typography>
                    </div>
                </div>

                {/* Painel direito */}
                <div className={classes.rightPanel}>
                    <div className={classes.formWrapper}>
                        <div className={classes.eyebrow}>Nova senha</div>

                        {success ? (
                            <div className={classes.successBox}>
                                <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
                                <Typography className={classes.title} style={{ fontSize: "1.5rem" }}>
                                    Senha redefinida!
                                </Typography>
                                <Typography className={classes.subtitle}>
                                    Sua nova senha foi salva com sucesso. Agora você pode fazer login normalmente.
                                </Typography>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    className={classes.submit}
                                    onClick={() => history.push("/login")}
                                >
                                    Ir para o login →
                                </Button>
                            </div>
                        ) : (
                            <>
                                <Typography className={classes.title}>
                                    Crie sua nova senha
                                </Typography>
                                <Typography className={classes.subtitle}>
                                    Escolha uma senha segura com pelo menos 6 caracteres.
                                </Typography>

                                {!token && (
                                    <div className={classes.errorBox}>
                                        Link inválido ou expirado. Solicite um novo link na tela de login.
                                    </div>
                                )}

                                {error && <div className={classes.errorBox}>{error}</div>}

                                <form onSubmit={handleSubmit}>
                                    <TextField
                                        label="Nova senha"
                                        type={showPwd ? "text" : "password"}
                                        fullWidth
                                        variant="outlined"
                                        margin="normal"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className={classes.textField}
                                        disabled={!token}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setShowPwd(v => !v)}>
                                                        {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <TextField
                                        label="Confirmar nova senha"
                                        type={showConfirm ? "text" : "password"}
                                        fullWidth
                                        variant="outlined"
                                        margin="normal"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className={classes.textField}
                                        disabled={!token}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setShowConfirm(v => !v)}>
                                                        {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        className={classes.submit}
                                        disabled={loading || !token}
                                    >
                                        {loading ? <CircularProgress size={20} color="inherit" /> : "Salvar nova senha"}
                                    </Button>
                                </form>

                                <a className={classes.backLink} onClick={() => history.push("/login")}>
                                    ← Voltar para o login
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResetPassword;
